import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Send, Mic, MicOff, Square, ArrowLeft, Volume2, CheckCircle2, MessageCircle, AudioLines, Play, Pause, ChevronRight } from 'lucide-react';
import { getQuestions, startSurvey, submitResponse, completeSurvey, getIntro, API_BASE_URL } from '../api';
import toast from 'react-hot-toast';

const SurveyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [questions, setQuestions] = useState([]);
  // If coming from HomePage with session, skip to questions directly
  // If coming from Agent with agentId, skip intro to format selection
  const incomingState = location.state;
  const [currentIndex, setCurrentIndex] = useState(
    incomingState?.sessionId ? 0 :
    incomingState?.agentId ? -1 :
    -2
  );
  const [sessionId, setSessionId] = useState(incomingState?.sessionId || null);
  const [responseFormat, setResponseFormat] = useState(incomingState?.format || null);
  const [answers, setAnswers] = useState({});
  const [textInput, setTextInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [introData, setIntroData] = useState({ text: '', audio_url: null });
  const [introPlaying, setIntroPlaying] = useState(false);
  const introAudioRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [matrixAnswers, setMatrixAnswers] = useState({});
  const [rankingSelection, setRankingSelection] = useState([]);
  const chatEndRef = useRef(null);
  const questionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const prevIndexRef = useRef(currentIndex);
  const questionAudioRef = useRef(null);
  const [isSpeaking, setIsSpeaking] = useState(false);

  // Single ref with all mutable state to avoid stale closure
  const stateRef = useRef({
    currentIndex: incomingState?.sessionId ? 0 : incomingState?.agentId ? -1 : -2,
    sessionId: incomingState?.sessionId || null,
    questions: [],
    audioBlob: null,
    audioUrl: null
  });

  useEffect(() => {
    stateRef.current.currentIndex = currentIndex;
    stateRef.current.sessionId = sessionId;
    stateRef.current.questions = questions;
    stateRef.current.audioBlob = audioBlob;
    stateRef.current.audioUrl = audioUrl;
  });

  // CRITICAL: only re-enable clicks AFTER React has re-rendered the next question
  useEffect(() => {
    if (submittingRef.current && currentIndex !== prevIndexRef.current) {
      document.body.style.pointerEvents = '';
      setSubmitting(false);
      submittingRef.current = false;
      prevIndexRef.current = currentIndex;
    }
  }, [currentIndex]);

  useEffect(() => { loadQuestions(); loadIntro(); }, []);
  useEffect(() => {
    // Wait for React to finish re-rendering the new question before scrolling
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
  }, [currentIndex]);
  useEffect(() => { submittingRef.current = submitting; }, [submitting]);

  const loadQuestions = async () => {
    try {
      const res = await getQuestions();
      setQuestions(res.data);
      stateRef.current.questions = res.data;
    } catch (err) {
      toast.error('Erreur de chargement');
    }
    setLoading(false);
  };

  const loadIntro = async () => {
    try {
      const res = await getIntro();
      setIntroData(res.data);
    } catch (err) {
      // silently fail
    }
  };

  const toggleIntroAudio = () => {
    if (!introAudioRef.current) return;
    if (introPlaying) {
      introAudioRef.current.pause();
      setIntroPlaying(false);
    } else {
      introAudioRef.current.play();
      setIntroPlaying(true);
    }
  };

  const handleFormatSelect = async (format) => {
    if (submittingRef.current) return;
    setSubmitting(true);
    submittingRef.current = true;
    document.body.style.pointerEvents = 'none';
    try {
      const source = incomingState?.invitationToken ? 'self_invite' : 'direct';
      const payload = { response_format: format, source };
      if (incomingState?.agentId) payload.agent_id = incomingState.agentId;
      const res = await startSurvey(payload);
      setSessionId(res.data.session_id);
      stateRef.current.sessionId = res.data.session_id;
      setCurrentIndex(0);
      stateRef.current.currentIndex = 0;
    } catch (err) {
      document.body.style.pointerEvents = '';
      setSubmitting(false);
      submittingRef.current = false;
      toast.error('Erreur de connexion');
    }
    // success: useEffect will reset submitting when currentIndex changes from -1 to 0
  };

  const handleSubmitAnswer = async (answer, isAudio = false) => {
    if (submittingRef.current) return;
    setSubmitting(true);
    submittingRef.current = true;
    document.body.style.pointerEvents = 'none';
    stopAllAudio();
    const state = stateRef.current;
    const idx = state.currentIndex;
    const sid = state.sessionId;
    const qList = state.questions;
    const question = qList[idx];
    if (!question || !sid) {
      setTimeout(() => {
        document.body.style.pointerEvents = '';
        setSubmitting(false);
        submittingRef.current = false;
      }, 200);
      return;
    }
    try {
      if (isAudio && state.audioBlob) {
        const formData = new FormData();
        formData.append('session_id', sid);
        formData.append('question_id', question.id);
        formData.append('audio', state.audioBlob, 'response.webm');
        formData.append('answer_text', '[Audio Response]');
        await submitResponse(formData);
      } else {
        await submitResponse({
          session_id: sid,
          question_id: question.id,
          answer_text: typeof answer === 'string' ? answer : null,
          answer_json: typeof answer === 'object' ? JSON.stringify(answer) : null
        });
      }
      setAnswers(prev => ({ ...prev, [question.id]: answer }));
      setTextInput('');
      setAudioBlob(null);
      setAudioUrl(null);
      setMatrixAnswers({});
      setRankingSelection([]);

      // Logique de saut conditionnel (skip_logic)
      let nextIndex = idx + 1;
      let skip = question.skip_logic;
      if (skip) {
        try { if (typeof skip === 'string') skip = JSON.parse(skip); } catch (e) { skip = null; }
      }
      if (skip && typeof answer === 'string' && skip[answer]) {
        const target = skip[answer];
        const targetOrder = typeof target === 'object' ? target.skip_to_order : target;
        if (targetOrder) {
          const targetIdx = qList.findIndex((q, i) => i > idx && q.question_order === Number(targetOrder));
          if (targetIdx !== -1) nextIndex = targetIdx;
        }
      }

      if (nextIndex < qList.length) {
        setCurrentIndex(nextIndex);
        stateRef.current.currentIndex = nextIndex;
      } else {
        await completeSurvey({ session_id: sid });
        document.body.style.pointerEvents = '';
        navigate('/merci');
      }
    } catch (err) {
      document.body.style.pointerEvents = '';
      setSubmitting(false);
      submittingRef.current = false;
      if (err.response?.status === 404) {
        toast.error('Session expirée. Redirection vers l\'accueil...');
        setTimeout(() => navigate('/', { replace: true }), 2000);
      } else {
        toast.error('Erreur lors de la soumission');
      }
    }
    // finally intentionally empty: submitting stays true until useEffect sees currentIndex change
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => chunksRef.current.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      toast.error('Impossible d\'acceder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const playQuestionAudio = (url) => {
    const fullUrl = url?.startsWith('http') ? url : `${API_BASE_URL}${url}`;
    const audio = new Audio(fullUrl);
    audio.play();
    return audio;
  };

  const speakText = (text) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'fr-FR';
    utterance.rate = 0.9;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    // Try to find a French voice
    const voices = window.speechSynthesis.getVoices();
    const frVoice = voices.find(v => v.lang.startsWith('fr'));
    if (frVoice) utterance.voice = frVoice;
    window.speechSynthesis.speak(utterance);
  };

  const stopAllAudio = () => {
    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
      questionAudioRef.current = null;
    }
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
  };

  // Auto-play question in audio mode
  useEffect(() => {
    if (responseFormat === 'audio' && currentQuestion && currentIndex >= 0) {
      // Small delay to let the UI render first
      const timer = setTimeout(() => {
        if (currentQuestion.audio_url) {
          const audio = playQuestionAudio(currentQuestion.audio_url);
          questionAudioRef.current = audio;
        } else {
          speakText(currentQuestion.question_text);
        }
      }, 400);
      return () => {
        clearTimeout(timer);
        stopAllAudio();
      };
    }
  }, [currentIndex, responseFormat]);

  const currentQuestion = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8faf8' }}>
        <div className="animate-pulse" style={{ color: '#2E7D32', fontSize: '18px' }}>Chargement...</div>
      </div>
    );
  }

  // ═══════ INTRO SCREEN ═══════
  if (currentIndex === -2) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg, #0d3d12 0%, #1B5E20 30%, #2E7D32 60%, #1a6b24 100%)', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-100px', right: '-100px', width: '350px', height: '350px', borderRadius: '50%', background: 'rgba(255,213,79,0.05)' }} />
        <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '250px', height: '250px', borderRadius: '50%', background: 'rgba(255,255,255,0.02)' }} />

        <button onClick={() => navigate('/')} style={{ position: 'absolute', top: '20px', left: '20px', background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', padding: '10px', borderRadius: '50%', cursor: 'pointer', zIndex: 10 }}>
          <ArrowLeft size={20} />
        </button>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', position: 'relative', zIndex: 5 }}>
          <div className="animate-fadeInUp" style={{ textAlign: 'center', maxWidth: '520px' }}>
            <img src="/logo.webp" alt="" style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'white', padding: '4px', marginBottom: '24px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }} />

            <div style={{ display: 'inline-block', background: 'rgba(255,213,79,0.12)', border: '1px solid rgba(255,213,79,0.25)', borderRadius: '50px', padding: '6px 18px', marginBottom: '20px' }}>
              <span style={{ color: '#FFD54F', fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase' }}>Commune de Khombole</span>
            </div>

            <h2 style={{ color: 'white', fontSize: '26px', fontFamily: 'Playfair Display, serif', marginBottom: '8px', lineHeight: '1.3' }}>
              Enquête de Satisfaction Citoyenne
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '28px' }}>Bilan du mandat municipal 2022–2027</p>

            {/* Intro audio player */}
            {introData.audio_url && (
              <div className="animate-fadeInUp" style={{ animationDelay: '0.2s', background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(10px)', borderRadius: '16px', padding: '20px', marginBottom: '24px', border: '1px solid rgba(255,255,255,0.12)' }}>
                <audio ref={introAudioRef} src={introData.audio_url ? `${API_BASE_URL}${introData.audio_url}` : ''} onEnded={() => setIntroPlaying(false)} />
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <button onClick={toggleIntroAudio} style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'linear-gradient(135deg, #FFD54F, #FFA000)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(255,193,7,0.3)', flexShrink: 0, transition: 'transform 0.2s' }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.08)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                    {introPlaying ? <Pause size={20} color="#1B5E20" /> : <Play size={20} color="#1B5E20" style={{ marginLeft: '2px' }} />}
                  </button>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <div style={{ color: 'white', fontSize: '13px', fontWeight: '600' }}>Écoutez l'introduction</div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginTop: '2px' }}>Message du Maire de Khombole</div>
                  </div>
                  <Volume2 size={16} color="rgba(255,255,255,0.3)" />
                </div>
              </div>
            )}

            {/* Intro text */}
            <div className="animate-fadeInUp" style={{ animationDelay: '0.3s', background: 'rgba(255,255,255,0.05)', borderRadius: '14px', padding: '20px', marginBottom: '32px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px', lineHeight: '1.8', fontStyle: 'italic' }}>
                {introData.text || 'Votre avis compte. Votre parole construit. Cette enquête est anonyme, volontaire et confidentielle.'}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #FFD54F, #FFA000)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1B5E20', fontWeight: '800', fontSize: '13px' }}>M</div>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ color: 'white', fontSize: '12px', fontWeight: '600' }}>Magueye BOYE</div>
                  <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px' }}>Maire de Khombole • magboye.com</div>
                </div>
              </div>
            </div>

            <button onClick={() => { if (introAudioRef.current) introAudioRef.current.pause(); setIntroPlaying(false); setCurrentIndex(-1); }}
              style={{ background: 'linear-gradient(135deg, #FFD54F 0%, #FFC107 50%, #FFB300 100%)', color: '#1B5E20', border: 'none', padding: '18px 48px', borderRadius: '60px', fontSize: '16px', fontWeight: '800', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '10px', boxShadow: '0 8px 36px rgba(255,193,7,0.35)', transition: 'all 0.3s' }}
              onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px) scale(1.03)'; }}
              onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0) scale(1)'; }}>
              Commencer <ChevronRight size={18} />
            </button>

            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', marginTop: '16px' }}>Durée estimée : 10 à 15 minutes</p>
          </div>
        </div>
      </div>
    );
  }

  // ═══════ FORMAT SELECTION SCREEN ═══════
  if (currentIndex === -1) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg, #0d3d12 0%, #1B5E20 30%, #2E7D32 60%, #1a6b24 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(255,213,79,0.04)' }} />
        <button onClick={() => setCurrentIndex(-2)} style={{ position: 'absolute', top: '20px', left: '20px', background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', padding: '10px', borderRadius: '50%', cursor: 'pointer', zIndex: 10 }}>
          <ArrowLeft size={20} />
        </button>
        <div className="animate-fadeInUp" style={{ textAlign: 'center', maxWidth: '500px', position: 'relative', zIndex: 5 }}>
          <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <MessageCircle size={26} color="#FFD54F" />
          </div>
          <h2 style={{ color: 'white', fontSize: '24px', marginBottom: '8px', fontFamily: 'Playfair Display, serif' }}>
            Comment souhaitez-vous répondre ?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '36px', fontSize: '14px' }}>
            Choisissez votre format de participation préféré
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <button onClick={() => handleFormatSelect('text')} style={{ background: 'white', border: 'none', borderRadius: '18px', padding: '24px 28px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '18px', transition: 'all 0.3s', boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}
              onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px) scale(1.01)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.15)'; }}
              onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0) scale(1)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.1)'; }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <MessageCircle size={24} color="#2E7D32" />
              </div>
              <div style={{ textAlign: 'left', flex: 1 }}>
                <div style={{ fontWeight: '700', fontSize: '16px', color: '#1a1a1a' }}>Format Texte</div>
                <div style={{ color: '#888', fontSize: '13px', marginTop: '3px' }}>Répondez en tapant vos réponses</div>
              </div>
              <ChevronRight size={18} color="#ccc" />
            </button>
            <button onClick={() => handleFormatSelect('audio')} style={{ background: 'white', border: 'none', borderRadius: '18px', padding: '24px 28px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '18px', transition: 'all 0.3s', boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}
              onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px) scale(1.01)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.15)'; }}
              onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0) scale(1)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.1)'; }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: 'linear-gradient(135deg, #FFF8E1, #FFECB3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <AudioLines size={24} color="#FF8F00" />
              </div>
              <div style={{ textAlign: 'left', flex: 1 }}>
                <div style={{ fontWeight: '700', fontSize: '16px', color: '#1a1a1a' }}>Format Audio</div>
                <div style={{ color: '#888', fontSize: '13px', marginTop: '3px' }}>Enregistrez vos réponses vocalement</div>
              </div>
              <ChevronRight size={18} color="#ccc" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f8faf8' }}>
      {/* Top Bar */}
      <div style={{ background: 'white', borderBottom: '1px solid #e8e8e8', padding: '12px 20px', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src="/logo.webp" alt="" style={{ width: '32px', height: '32px', borderRadius: '50%' }} />
            <div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#1B5E20' }}>Enquete Khombole</div>
              <div style={{ fontSize: '11px', color: '#888' }}>{currentQuestion?.section}</div>
            </div>
          </div>
          <div style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>
            {currentIndex + 1} / {questions.length}
          </div>
        </div>
        <div style={{ height: '4px', background: '#E8F5E9', borderRadius: '2px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg, #2E7D32, #4CAF50)', borderRadius: '2px', transition: 'width 0.5s ease' }} />
        </div>
      </div>

      {/* Chat Area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px', maxWidth: '700px', width: '100%', margin: '0 auto' }}>
        {/* Section indicator */}
        {(currentIndex === 0 || questions[currentIndex]?.section !== questions[currentIndex - 1]?.section) && (
          <div className="animate-fadeInUp" style={{ textAlign: 'center', margin: '10px 0 20px' }}>
            <span style={{ background: '#E8F5E9', color: '#2E7D32', padding: '6px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
              {currentQuestion?.section}
            </span>
          </div>
        )}

        {/* Submitting overlay */}
        {submitting && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, cursor: 'wait' }} />
        )}

        {/* Question bubble */}
        <div ref={questionRef} className="animate-slideIn" key={currentIndex} style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #2E7D32, #4CAF50)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <MessageCircle size={16} color="white" />
          </div>
          <div style={{ background: 'white', borderRadius: '16px 16px 16px 4px', padding: '16px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', maxWidth: '85%' }}>
            {responseFormat === 'audio' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <span style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#FF8F00', background: '#FFF8E1', padding: '2px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <Volume2 size={10} /> Mode audio
                </span>
                {isSpeaking && (
                  <span style={{ fontSize: '10px', color: '#999', display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#E53935', animation: 'pulse 1s infinite' }} />
                    Lecture en cours...
                  </span>
                )}
              </div>
            )}
            <p style={{ fontSize: '15px', lineHeight: '1.6', color: '#1a1a1a', fontWeight: '500' }}>
              {currentQuestion?.question_text}
            </p>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
              {currentQuestion?.audio_url && (
                <button onClick={() => { stopAllAudio(); const a = playQuestionAudio(currentQuestion.audio_url); questionAudioRef.current = a; }} style={{ background: '#E8F5E9', border: 'none', borderRadius: '20px', padding: '6px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#2E7D32' }}>
                  <Volume2 size={14} /> {responseFormat === 'audio' ? 'Répéter' : 'Ecouter la question'}
                </button>
              )}
              {responseFormat === 'audio' && !currentQuestion?.audio_url && (
                <button onClick={() => { stopAllAudio(); speakText(currentQuestion.question_text); }} style={{ background: '#FFF8E1', border: 'none', borderRadius: '20px', padding: '6px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#FF8F00' }}>
                  <Volume2 size={14} /> Répéter la lecture
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Answer options */}
        <div style={{ paddingLeft: '46px' }} key={`options-${currentIndex}`}>
          {renderAnswerOptions(currentQuestion, textInput, setTextInput, handleSubmitAnswer, matrixAnswers, setMatrixAnswers, rankingSelection, setRankingSelection, submitting, responseFormat, isRecording, startRecording, stopRecording, audioUrl, audioBlob, playQuestionAudio)}
        </div>

        <div ref={chatEndRef} />
      </div>
    </div>
  );
};

function renderAnswerOptions(question, textInput, setTextInput, handleSubmitAnswer, matrixAnswers, setMatrixAnswers, rankingSelection, setRankingSelection, submitting, responseFormat, isRecording, startRecording, stopRecording, audioUrl, audioBlob, playQuestionAudio) {
  if (!question) return null;
  const options = question.options ? (typeof question.options === 'string' ? JSON.parse(question.options) : question.options) : [];
  const optionsAudio = question.options_audio ? (typeof question.options_audio === 'string' ? JSON.parse(question.options_audio) : question.options_audio) : [];

  const buttonStyle = (selected) => ({
    background: selected ? 'linear-gradient(135deg, #2E7D32, #388E3C)' : 'white',
    color: selected ? 'white' : '#333',
    border: selected ? 'none' : '1.5px solid #e0e0e0',
    borderRadius: '12px',
    padding: '14px 18px',
    cursor: 'pointer',
    fontSize: '14px',
    textAlign: 'left',
    transition: 'all 0.2s',
    width: '100%',
    fontWeight: selected ? '600' : '400'
  });

  // Single choice
  if (question.question_type === 'single_choice') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {Array.isArray(options) && options.map((opt, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={() => handleSubmitAnswer(opt)} disabled={submitting} style={{ ...buttonStyle(false), flex: 1 }}
              onMouseOver={(e) => { e.currentTarget.style.borderColor = '#4CAF50'; e.currentTarget.style.background = '#f1f8f1'; }}
              onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.background = 'white'; }}>
              <span style={{ display: 'inline-block', width: '24px', height: '24px', borderRadius: '50%', border: '2px solid #ccc', marginRight: '12px', verticalAlign: 'middle', lineHeight: '20px', textAlign: 'center', fontSize: '12px', color: '#999' }}>
                {String.fromCharCode(65 + i)}
              </span>
              {opt}
            </button>
            {optionsAudio[i] && (
              <button onClick={() => playQuestionAudio(optionsAudio[i])} style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#E3F2FD', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} title="Ecouter">
                <Volume2 size={16} color="#1565C0" />
              </button>
            )}
          </div>
        ))}
        {responseFormat === 'audio' && renderAudioInput(isRecording, startRecording, stopRecording, audioUrl, audioBlob, handleSubmitAnswer)}
      </div>
    );
  }

  // Text input
  if (question.question_type === 'text') {
    return (
      <div>
        {responseFormat === 'audio' && renderAudioInput(isRecording, startRecording, stopRecording, audioUrl, audioBlob, handleSubmitAnswer)}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
          <textarea value={textInput} onChange={(e) => setTextInput(e.target.value)} placeholder="Tapez votre reponse ici..."
            style={{ flex: 1, border: '1.5px solid #e0e0e0', borderRadius: '12px', padding: '14px', fontSize: '14px', resize: 'none', minHeight: '80px', outline: 'none', fontFamily: 'Inter, sans-serif', transition: 'border-color 0.2s' }}
            onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'} />
          <button onClick={() => { if (textInput.trim()) handleSubmitAnswer(textInput.trim()); }} disabled={submitting || !textInput.trim()}
            style={{ background: textInput.trim() ? 'linear-gradient(135deg, #2E7D32, #4CAF50)' : '#e0e0e0', border: 'none', borderRadius: '12px', padding: '14px', cursor: textInput.trim() ? 'pointer' : 'default', transition: 'all 0.2s' }}>
            <Send size={18} color="white" />
          </button>
        </div>
        <button onClick={() => handleSubmitAnswer('')} disabled={submitting}
          style={{ marginTop: '10px', background: 'none', border: 'none', color: '#888', fontSize: '12px', cursor: 'pointer', fontWeight: '500', padding: '4px 0', display: 'flex', alignItems: 'center', gap: '4px', transition: 'color 0.2s' }}
          onMouseOver={(e) => e.currentTarget.style.color = '#4CAF50'}
          onMouseOut={(e) => e.currentTarget.style.color = '#888'}>
          <span style={{ fontSize: '14px' }}>&#8250;</span> Je ne peux pas repondre a cette question
        </button>
      </div>
    );
  }

  // Rating matrix
  if (question.question_type === 'rating_matrix') {
    const { domains, ratings } = options;
    const allAnswered = domains && domains.every(d => matrixAnswers[d]);

    const ratingColors = {
      'Très satisfait(e)': { bg: '#E8F5E9', border: '#4CAF50', text: '#1B5E20', dot: '#2E7D32' },
      'Satisfait(e)': { bg: '#E3F2FD', border: '#42A5F5', text: '#0D47A1', dot: '#1976D2' },
      'Peu satisfait(e)': { bg: '#FFF3E0', border: '#FFA726', text: '#E65100', dot: '#F57C00' },
      'Pas du tout satisfait(e)': { bg: '#FFEBEE', border: '#EF5350', text: '#B71C1C', dot: '#D32F2F' },
      'Sans avis': { bg: '#F5F5F5', border: '#BDBDBD', text: '#424242', dot: '#757575' }
    };

    return (
      <div>
        <div style={{ marginBottom: '16px', background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)', borderRadius: '12px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#2E7D32', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: 'white', fontSize: '14px', fontWeight: '700' }}>{Object.keys(matrixAnswers).length}</span>
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#1B5E20' }}>{Object.keys(matrixAnswers).length} sur {domains?.length || 0} domaines évalués</div>
            <div style={{ fontSize: '11px', color: '#4CAF50', fontWeight: '500' }}>
              {allAnswered ? 'Tous les domaines sont évalués ! Vous pouvez valider.' : 'Cliquez sur une note pour chaque domaine ci-dessous'}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          {domains && domains.map((domain, i) => {
            const selectedRating = matrixAnswers[domain];
            const isComplete = !!selectedRating;
            return (
              <div key={i} data-domain={domain} style={{
                background: isComplete ? 'white' : '#fafafa',
                border: isComplete ? '1.5px solid #C8E6C9' : '1.5px solid #eee',
                borderRadius: '14px',
                padding: '16px',
                marginBottom: '10px',
                transition: 'all 0.2s',
                boxShadow: isComplete ? '0 2px 8px rgba(46,125,50,0.08)' : 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <div style={{
                    width: '22px', height: '22px', borderRadius: '50%',
                    background: isComplete ? '#2E7D32' : '#E0E0E0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', color: 'white', fontWeight: '700', flexShrink: 0
                  }}>
                    {isComplete ? '✓' : (i + 1)}
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a', lineHeight: '1.4' }}>{domain}</div>
                  {isComplete && (
                    <span style={{
                      marginLeft: 'auto', fontSize: '11px', fontWeight: '600',
                      padding: '4px 10px', borderRadius: '20px',
                      background: ratingColors[selectedRating]?.bg || '#f0f0f0',
                      color: ratingColors[selectedRating]?.text || '#333',
                      border: `1px solid ${ratingColors[selectedRating]?.border || '#ddd'}`,
                      whiteSpace: 'nowrap'
                    }}>
                      {selectedRating}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', paddingLeft: '30px' }}>
                  {ratings && ratings.map((r, j) => {
                    const colors = ratingColors[r] || { bg: '#f5f5f5', border: '#ddd', text: '#555', dot: '#999' };
                    const isSelected = selectedRating === r;
                    return (
                      <button key={j} onClick={() => {
                          const updated = { ...matrixAnswers, [domain]: r };
                          setMatrixAnswers(updated);
                          setTimeout(() => {
                            const currentIdx = domains.indexOf(domain);
                            const nextDomain = domains.slice(currentIdx + 1).find(d => !updated[d]);
                            if (nextDomain) {
                              document.querySelector(`[data-domain="${nextDomain}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                          }, 200);
                        }}
                        style={{
                          padding: '8px 14px', borderRadius: '20px',
                          border: isSelected ? `2px solid ${colors.border}` : '1.5px solid #e0e0e0',
                          background: isSelected ? colors.bg : 'white',
                          color: isSelected ? colors.text : '#666',
                          fontSize: '12px', cursor: 'pointer', fontWeight: isSelected ? '600' : '500',
                          transition: 'all 0.2s',
                          boxShadow: isSelected ? `0 2px 8px ${colors.bg}` : 'none',
                          display: 'flex', alignItems: 'center', gap: '5px'
                        }}
                        onMouseOver={(e) => { if (!isSelected) { e.currentTarget.style.borderColor = colors.border; e.currentTarget.style.background = colors.bg; } }}
                        onMouseOut={(e) => { if (!isSelected) { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.background = 'white'; } }}>
                        <span style={{
                          width: '8px', height: '8px', borderRadius: '50%',
                          background: isSelected ? colors.dot : '#ddd',
                          display: 'inline-block', transition: 'all 0.2s'
                        }} />
                        {r}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <button onClick={() => handleSubmitAnswer(matrixAnswers)} disabled={!allAnswered || submitting}
          style={{ width: '100%', padding: '16px', borderRadius: '14px', border: 'none',
            background: allAnswered ? 'linear-gradient(135deg, #2E7D32, #4CAF50)' : '#e0e0e0',
            color: 'white', fontWeight: '700', cursor: allAnswered ? 'pointer' : 'default',
            fontSize: '15px', boxShadow: allAnswered ? '0 4px 16px rgba(46,125,50,0.3)' : 'none',
            transition: 'all 0.3s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
          }}
          onMouseOver={(e) => { if (allAnswered) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(46,125,50,0.4)'; } }}
          onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = allAnswered ? '0 4px 16px rgba(46,125,50,0.3)' : 'none'; }}>
          <CheckCircle2 size={18} />
          Valider mes évaluations
        </button>
      </div>
    );
  }

  // Ranking
  if (question.question_type === 'ranking') {
    return (
      <div>
        <p style={{ fontSize: '12px', color: '#888', marginBottom: '10px' }}>Selectionnez vos 3 priorites par ordre d'importance</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {Array.isArray(options) && options.map((opt, i) => {
            const rank = rankingSelection.indexOf(opt) + 1;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button onClick={() => {
                  if (rank > 0) {
                    setRankingSelection(prev => prev.filter(x => x !== opt));
                  } else if (rankingSelection.length < 3) {
                    setRankingSelection(prev => [...prev, opt]);
                  }
                }} style={{ ...buttonStyle(rank > 0), display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                  <span style={{ width: '28px', height: '28px', borderRadius: '50%', background: rank > 0 ? '#FFD54F' : '#f0f0f0', color: rank > 0 ? '#1B5E20' : '#999', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '13px', flexShrink: 0 }}>
                    {rank > 0 ? rank : '-'}
                  </span>
                  {opt}
                </button>
                {optionsAudio[i] && (
                  <button onClick={() => playQuestionAudio(optionsAudio[i])} style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#E3F2FD', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} title="Ecouter">
                    <Volume2 size={16} color="#1565C0" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <button onClick={() => handleSubmitAnswer({ priorities: rankingSelection })} disabled={rankingSelection.length !== 3 || submitting}
          style={{ width: '100%', marginTop: '12px', padding: '14px', borderRadius: '12px', border: 'none', background: rankingSelection.length === 3 ? 'linear-gradient(135deg, #2E7D32, #4CAF50)' : '#e0e0e0', color: 'white', fontWeight: '600', cursor: rankingSelection.length === 3 ? 'pointer' : 'default', fontSize: '14px' }}>
          Valider mes priorites ({rankingSelection.length}/3)
        </button>
      </div>
    );
  }

  // Contact
  if (question.question_type === 'contact') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {Array.isArray(options) && options.map((opt, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={() => handleSubmitAnswer(opt)} disabled={submitting} style={{ ...buttonStyle(false), flex: 1 }}
              onMouseOver={(e) => { e.currentTarget.style.borderColor = '#4CAF50'; }}
              onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e0e0e0'; }}>
              {opt}
            </button>
            {optionsAudio[i] && (
              <button onClick={() => playQuestionAudio(optionsAudio[i])} style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#E3F2FD', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} title="Ecouter">
                <Volume2 size={16} color="#1565C0" />
              </button>
            )}
          </div>
        ))}
      </div>
    );
  }

  return null;
}

function renderAudioInput(isRecording, startRecording, stopRecording, audioUrl, audioBlob, handleSubmitAnswer) {
  return (
    <div style={{ background: '#FFF8E1', borderRadius: '12px', padding: '14px', marginBottom: '12px', border: '1px solid #FFECB3' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {!isRecording && !audioUrl && (
          <button onClick={startRecording} style={{ background: 'linear-gradient(135deg, #FF8F00, #FFA000)', border: 'none', borderRadius: '50%', width: '44px', height: '44px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Mic size={20} color="white" />
          </button>
        )}
        {isRecording && (
          <button onClick={stopRecording} style={{ background: '#E53935', border: 'none', borderRadius: '50%', width: '44px', height: '44px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'pulse 1.5s infinite' }}>
            <Square size={16} color="white" />
          </button>
        )}
        {isRecording && <span style={{ fontSize: '13px', color: '#E53935', fontWeight: '600' }}>Enregistrement en cours...</span>}
        {audioUrl && !isRecording && (
          <>
            <audio src={audioUrl} controls style={{ flex: 1, height: '36px' }} />
            <button onClick={() => handleSubmitAnswer(null, true)} style={{ background: 'linear-gradient(135deg, #2E7D32, #4CAF50)', border: 'none', borderRadius: '10px', padding: '10px 16px', cursor: 'pointer', color: 'white', fontSize: '13px', fontWeight: '600' }}>
              <Send size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Envoyer
            </button>
          </>
        )}
        {!isRecording && !audioUrl && <span style={{ fontSize: '12px', color: '#888' }}>Ou enregistrez votre reponse audio</span>}
      </div>
    </div>
  );
}

export default SurveyPage;
